"use client";

import {
  CheckCircleIcon,
  ShieldCheckIcon,
  WarningCircleIcon,
  XCircleIcon,
} from "@phosphor-icons/react";
import { type ReactNode, useCallback, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Typography } from "@/components/ui/typography";
import { useTranslation } from "@/i18n/client";
import { cn } from "@/utils/shared";
import SecondaryButton from "./secondary-button";

interface CommonDialogProps {
  children?: ReactNode;
  description?: ReactNode;
  onPrimaryButtonClick?: () => void;
  onSecondaryButtonClick?: () => void;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  title: string | ReactNode;
  trigger?: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  iconVariant?: "success" | "warning" | "error" | "security";
  icon?: ReactNode;
  loading?: boolean;
  onPointerDownOutside?: (event: Event) => void;
  secondaryButtonDisabled?: boolean;
  primaryButtonDisabled?: boolean;
  customDialogFooter?: ReactNode; // Custom footer to render instead of default footer
  childrenClassName?: string;
  contentClassName?: string;
  scrollable?: boolean;
  hasCloseButton?: boolean;
  closeClassName?: string;
  iconContainerClassName?: string;
}

const iconConfig = {
  success: {
    icon: CheckCircleIcon,
    bgColor: "bg-success-100",
    iconColor: "text-success-400",
  },
  warning: {
    icon: WarningCircleIcon,
    bgColor: "bg-warning-100",
    iconColor: "text-warning-400",
  },
  error: {
    icon: XCircleIcon,
    bgColor: "bg-error-100",
    iconColor: "text-error-400",
  },
  security: {
    icon: ShieldCheckIcon,
    bgColor: "bg-primary-100",
    iconColor: "text-primary-600",
  },
};

export default function CommonDialog(props: CommonDialogProps) {
  const {
    children,
    description,
    onPrimaryButtonClick,
    onSecondaryButtonClick,
    primaryButtonText,
    secondaryButtonText,
    title,
    trigger,
    defaultOpen,
    open,
    onOpenChange,
    modal,
    iconVariant,
    icon,
    loading = false,
    onPointerDownOutside,
    secondaryButtonDisabled,
    primaryButtonDisabled,
    customDialogFooter,
    childrenClassName,
    contentClassName,
    scrollable = true,
    hasCloseButton,
    closeClassName,
    iconContainerClassName,
  } = props;
  const { t: tCommonDialog } = useTranslation("common-dialog");
  const [containerHeight, setContainerHeight] = useState(0);
  const setContainerRef = useCallback((node: HTMLDivElement) => {
    if (node) {
      setContainerHeight(node.clientHeight);
      const resizeObserver = new ResizeObserver((entries) => {
        if (entries[0]) {
          setContainerHeight(entries[0].target.clientHeight);
        }
      });
      resizeObserver.observe(node);
    }
  }, []);

  const RenderIcon = () => {
    if (!iconVariant) {
      return null;
    }
    const config = iconConfig[iconVariant];
    const IconComponent = config.icon;

    return (
      <div
        className={`rounded-full p-[6px] ${config.bgColor} flex items-center justify-center`}
      >
        <IconComponent size={20} className={config.iconColor} />
      </div>
    );
  };

  return (
    <Dialog defaultOpen={defaultOpen} open={open} onOpenChange={onOpenChange} modal={modal}>
      {trigger && <DialogTrigger asChild={true}>{trigger}</DialogTrigger>}
      <DialogContent
        onPointerDownOutside={(e) => {
          if (loading) {
            e.preventDefault();
          }
          onPointerDownOutside?.(e);
        }}
        className={cn(
          "gap-0 max-w-[calc(100%-2rem)] lg:max-w-lg bg-neutral-0 border-none rounded-xl shadow-lg p-5",
          contentClassName
        )}
        hasCloseButton={hasCloseButton}
        closeClassName={closeClassName}
      >
        <div className="flex flex-row gap-3 items-start">
          {icon ? (
            <div className={iconContainerClassName}>{icon}</div>
          ) : iconVariant ? (
            <div className={iconContainerClassName}>
              <RenderIcon />
            </div>
          ) : null}
          <div className="flex-1 w-full">
            <DialogHeader className="text-left mb-4">
              <DialogTitle>
                {typeof title === "string" ? (
                  <Typography className="text-neutral-700" variant="heading 3">
                    {title}
                  </Typography>
                ) : (
                  title
                )}
              </DialogTitle>
              {description && <DialogDescription>{description}</DialogDescription>}
              {!description && (
                <DialogDescription className="sr-only !p-0">
                  {tCommonDialog("standardCommonDialogDescription")}
                </DialogDescription>
              )}
            </DialogHeader>
            {children && (
              <div
                ref={setContainerRef}
                className={childrenClassName ?? "mb-6 max-h-[50vh] flex-1 min-h-0 overflow-hidden"}
              >
                {scrollable ? (
                  <ScrollArea
                    className="h-full"
                    style={{
                      height: containerHeight ? `${containerHeight}px` : "100%",
                    }}
                    type="always"
                  >
                    {children}
                  </ScrollArea>
                ) : (
                  children
                )}
              </div>
            )}

            {customDialogFooter ? (
              customDialogFooter
            ) : (
              <DialogFooter className="gap-2">
                {!!secondaryButtonText && (
                  <SecondaryButton
                    secondaryButtonText={secondaryButtonText}
                    onSecondaryButtonClick={onSecondaryButtonClick}
                    disabled={secondaryButtonDisabled}
                  />
                )}
                {!!primaryButtonText && (
                  <Button
                    onClick={onPrimaryButtonClick}
                    size="md"
                    variant="primary"
                    disabled={loading || primaryButtonDisabled}
                    loading={loading}
                  >
                    {primaryButtonText}
                  </Button>
                )}
              </DialogFooter>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
